/**
 * pageMeta.js — what search engines are told about each page.
 *
 * Clincordant is a single-page app: every URL is served the same index.html, so the
 * title, description, canonical link and robots rule in that file would
 * otherwise apply to every page alike. That is how the whole site ended up
 * carrying `noindex, nofollow` — a line meant to keep private pages out of
 * search, sitting in the one file every public page is built from, telling
 * Google not to index anything at all.
 *
 * Now index.html allows indexing, and the app sets the rules per route after it
 * loads. Google renders JavaScript before deciding, so it sees these values:
 *
 *   public pages (the landing page, the legal documents, support)
 *       indexable, with a canonical URL on clincordant.com — which also settles the
 *       duplicate copies Firebase serves on its own web.app and firebaseapp.com
 *       domains;
 *   everything else (the signed-in app, sign-in, onboarding)
 *       `noindex`. Those pages are behind a login; a crawler only ever sees the
 *       sign-in screen there, and it should not be in search results.
 */

export const SITE_URL = 'https://clincordant.com';
export const DEFAULT_TITLE = 'Clincordant — clinical evidence, personalised';
export const DEFAULT_DESCRIPTION =
  'The new medical research that matters to your specialty, in minutes a day, plus answers from the literature and a network for clinicians.';

/** Paths a search engine may index. Everything else is private or a form. */
export function isIndexable(pathname) {
  const p = String(pathname || '/').replace(/\/+$/, '') || '/';
  return p === '/' || p === '/legal' || p.startsWith('/legal/') || p === '/support';
}

function metaTag(name) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  return el;
}

function canonicalLink() {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  return el;
}

/** The robots rule and canonical URL for a path. Called on every navigation. */
export function applyRouteRules(pathname) {
  const index = isIndexable(pathname);
  const robots = document.head.querySelector('meta[name="robots"]');

  if (index) {
    robots?.remove();
    const clean = String(pathname || '/').replace(/\/+$/, '') || '/';
    canonicalLink().setAttribute('href', `${SITE_URL}${clean === '/' ? '/' : clean}`);
  } else {
    metaTag('robots').setAttribute('content', 'noindex');
    document.head.querySelector('link[rel="canonical"]')?.remove();
  }
}

/** A page's own title and description. Pages without one get the defaults. */
export function setPageMeta({ title, description } = {}) {
  document.title = title || DEFAULT_TITLE;
  metaTag('description').setAttribute('content', description || DEFAULT_DESCRIPTION);
}
